import { 
  Home, 
  Car, 
  Tags, 
  Wrench, 
  ChevronDown,
  User,
  PlusCircle,
  Menu,
  X,
  Building,
  PawPrint,
  Users,
  Store,
  Heart,
  LogOut
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/AuthContext';
import { useState, useEffect } from 'react';
import { LogoutConfirmationModal } from './LogoutConfirmationModal';
import Modal from './Modal';
import AuthModal from './AuthModal';
import { preloadCategories } from '../hooks/useCategories';
import type { NavbarProps } from '../types';

const NAV_LINKS = [
  { name: 'Home', href: '/', icon: Home },
  { name: 'Cars & Vehicles', href: '/category/cars-vehicles', icon: Car },
  { name: 'For Sale', href: '/category/for-sale', icon: Tags },
  { name: 'Services', href: '/category/services', icon: Wrench },
] as const;

const MORE_LINKS = [
  { name: 'Property', href: '/category/property', icon: Building },
  { name: 'Pets', href: '/category/pets', icon: PawPrint },
  { name: 'Jobs', href: '/category/jobs', icon: Users },
  { name: 'Community', href: '/category/community', icon: Users },
  { name: 'Store', href: '/category/store', icon: Store },
] as const;

const MOBILE_LINKS = [
  { name: 'Home', href: '/', icon: Home },
  { name: 'Cars & Vehicles', href: '/category/cars-vehicles', icon: Car },
  { name: 'For Sale', href: '/category/for-sale', icon: Tags },
  { name: 'Services', href: '/category/services', icon: Wrench },
  ...MORE_LINKS,
] as const;

export default function Navbar({ mobileOpen, onToggleMobile }: NavbarProps): JSX.Element {
  const { isAuthenticated, user, logout, getDisplayName } = useAuth();
  const navigate = useNavigate();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authView, setAuthView] = useState<'signin' | 'signup'>('signin');
  const [imageError, setImageError] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const displayName = getDisplayName();

  // Reset image error when user changes
  useEffect(() => {
    setImageError(false);
  }, [user?.image]);

  const handlePostAd = () => {
    if (isAuthenticated) {
      navigate('/post-ad');
    } else {
      openAuthModal('signin');
    }
  };

  // Prefetch categories on hover for instant PostAdPage loading
  const handlePostAdHover = () => {
    if (isAuthenticated) {
      preloadCategories();
    }
  };
  
  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleConfirmLogout = () => {
    logout();
    setShowLogoutModal(false);
  };

  const openAuthModal = (view: 'signin' | 'signup') => {
    setAuthView(view);
    setShowAuthModal(true);
  };

  return (
    <header role="banner" className="sticky top-0 z-50">
      <nav
        aria-label="Main navigation"
        className="bg-dark-900/95 backdrop-blur-xl border-b border-dark-800"
      >
        {/* Main bar */}
        <div className="nav-inner h-16 px-4 sm:px-6 flex items-center justify-between max-w-7xl mx-auto">
          {/* Logo + desktop links */}
          <div className="flex items-center gap-8">
            <Link to="/" aria-label="AdsWorld Home" className="flex items-center group">
              <img 
                  src="/adsworld-logo-transparent.svg" 
                  alt="AdsWorld Logo" 
                  className="w-auto h-12 object-contain"
                />
            </Link>

            <div className="nav-links hidden lg:flex items-center gap-1">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  onMouseEnter={preloadCategories}
                  className="nav-link flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-white/5 transition-colors"
                >
                  <link.icon className="w-4 h-4" />
                  {link.name}
                </Link>
              ))}
              
              {/* More dropdown */}
              <div className="relative">
                <button
                  type="button"
                  aria-expanded={isMoreMenuOpen}
                  aria-controls="more-navigation-menu"
                  onClick={() => setIsMoreMenuOpen((open) => !open)}
                  className="nav-link flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-white/5 transition-colors"
                >
                  More
                  <ChevronDown className="w-4 h-4" />
                </button>
                {isMoreMenuOpen && (
                <div id="more-navigation-menu" className="absolute top-full left-0 mt-2 w-48 bg-dark-800 rounded-xl shadow-xl border border-dark-700">
                  {MORE_LINKS.map((link) => (
                    <Link
                      key={link.name}
                      to={link.href}
                      onClick={() => setIsMoreMenuOpen(false)}
                      onMouseEnter={preloadCategories}
                      className="flex items-center gap-3 px-4 py-3 text-dark-300 hover:text-white hover:bg-dark-700/50 first:rounded-t-xl last:rounded-b-xl transition-colors"
                    >
                      <link.icon className="w-4 h-4" />
                      {link.name}
                    </Link>
                  ))}
                </div>
                )}
              </div>
            </div>
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-3">
            {isAuthenticated && user ? (
              <div className="relative">
                <button
                  type="button"
                  aria-expanded={isUserMenuOpen}
                  aria-controls="user-navigation-menu"
                  onClick={() => setIsUserMenuOpen((open) => !open)}
                  className="hidden sm:flex items-center gap-2 text-dark-300 hover:text-white px-3 py-2 rounded-lg hover:bg-white/5 transition-all text-sm font-medium"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white text-sm font-bold">
                    {user.image && !imageError ? (
                      <img
                        src={user.image}
                        alt={displayName}
                        className="w-full h-full rounded-full object-cover"
                        onError={() => setImageError(true)}
                      />
                    ) : (
                      displayName.charAt(0).toUpperCase()
                    )}
                  </div>
                  <span className="max-w-[120px] truncate">{displayName}</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                
                {/* User dropdown */}
                {isUserMenuOpen && (
                <div id="user-navigation-menu" className="absolute top-full right-0 mt-2 w-48 bg-dark-800 rounded-xl shadow-xl border border-dark-700">
                  <Link
                    to="/profile"
                    onClick={() => setIsUserMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-dark-300 hover:text-white hover:bg-dark-700/50 first:rounded-t-xl transition-colors"
                  >
                    <User className="w-4 h-4" />
                    My Profile
                  </Link>
                  <Link
                    to="/profile/my-ads"
                    onClick={() => setIsUserMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-dark-300 hover:text-white hover:bg-dark-700/50 transition-colors"
                  >
                    <Store className="w-4 h-4" />
                    My Ads
                  </Link>
                  <Link
                    to="/profile/saved"
                    onClick={() => setIsUserMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-dark-300 hover:text-white hover:bg-dark-700/50 transition-colors"
                  >
                    <Heart className="w-4 h-4" />
                    Saved Items
                  </Link>
                  <button
                    onClick={handleLogoutClick}
                    className="w-full flex items-center gap-3 px-4 py-3 text-red-400 hover:text-red-300 hover:bg-dark-700/50 last:rounded-b-xl transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </div>
                )}
              </div>
            ) : (
              <button 
                onClick={() => openAuthModal('signin')}
                className="hidden sm:flex items-center gap-2 text-dark-300 hover:text-white px-4 py-2 rounded-lg hover:bg-white/5 transition-all text-sm font-medium"
              >
                <User className="w-4 h-4" />
                Sign In
              </button>
            )}
            
            <button 
              onClick={handlePostAd}
              onMouseEnter={handlePostAdHover}
              className="post-btn post-nav-btn hidden sm:flex items-center gap-2 py-2.5 px-4 text-sm"
            >
              <PlusCircle className="w-4 h-4" />
              Post Ad
            </button>

            {/* Mobile menu button */}
            <button
              className="lg:hidden flex items-center justify-center w-10 h-10 rounded-xl hover:bg-white/10 transition-colors"
              onClick={onToggleMobile}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? (
                <X className="w-5 h-5 text-white" />
              ) : (
                <Menu className="w-5 h-5 text-white" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile dropdown */}
        <div
          className={`mobile-nav lg:hidden ${mobileOpen ? 'open' : ''}`}
          role="navigation"
          aria-label="Mobile navigation"
        >
          {MOBILE_LINKS.map((link) => (
            <Link
              key={link.name}
              to={link.href}
              onMouseEnter={preloadCategories}
              className="mobile-nav-link flex items-center gap-3"
            >
              <link.icon className="w-5 h-5" />
              {link.name}
            </Link>
          ))}
          <div className="mt-4 pt-4 border-t border-dark-700 flex flex-col gap-3">
            {isAuthenticated && user ? (
              <>
                <Link 
                  to="/profile"
                  className="flex items-center gap-3 px-4 py-3 text-dark-300 hover:text-white rounded-xl hover:bg-dark-700/50 transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white text-sm font-bold">
                    {user.image && !imageError ? (
                      <img
                        src={user.image}
                        alt={displayName}
                        className="w-full h-full rounded-full object-cover"
                        onError={() => setImageError(true)}
                      />
                    ) : (
                      displayName.charAt(0).toUpperCase()
                    )}
                  </div>
                  <span>{displayName}</span>
                </Link>
                <button 
                  onClick={handleLogoutClick}
                  className="flex items-center justify-center gap-2 text-red-400 hover:text-red-300 py-3 rounded-xl hover:bg-dark-700/50 transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                  Logout
                </button>
              </>
            ) : (
              <button 
                onClick={() => openAuthModal('signin')}
                className="flex items-center justify-center gap-2 text-dark-300 hover:text-white py-3 rounded-xl hover:bg-dark-700/50 transition-colors"
              >
                <User className="w-5 h-5" />
                Sign In
              </button>
            )}
            <button 
              onClick={handlePostAd}
              onMouseEnter={handlePostAdHover}
              className="post-btn w-full flex items-center justify-center gap-2 py-3"
            >
              <PlusCircle className="w-5 h-5" />
              Post Your Ad
            </button>
          </div>
        </div>
      </nav>

      {/* Logout Confirmation Modal */}
      <LogoutConfirmationModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleConfirmLogout}
        userName={displayName}
      />

      {/* Auth Modal */}
      <Modal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} size="auth">
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          initialView={authView}
        />
      </Modal>
    </header>
  );
}
